import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import "./CreateEditParticipantsPage.css"; // Crear este archivo CSS

function CreateEditParticipantPage() {
  const [documentNumber, setDocumentNumber] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  // No necesitamos un estado para participant_type_id aquí, ya que no se guarda en la tabla participants directamente.
  // Solo se usa al crear un CERTIFICADO (en uploadController.js).

  const [isLoading, setIsLoading] = useState(true); // Para cargar datos iniciales (del participante si es edición)
  const [isSaving, setIsSaving] = useState(false); // Para indicar que se está guardando
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successPopupMessage, setSuccessPopupMessage] = useState("");

  const { id: participantId } = useParams(); // Obtiene el ID del participante de la URL (si es modo edición)
  const navigate = useNavigate();
  const { token } = useAuth();

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // useEffect: Carga datos del participante si es modo edición
  useEffect(() => {
    const fetchParticipantData = async () => {
      if (!participantId) {
        // Si no hay ID, es modo creación, no hay datos que cargar
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError("");
      try {
        const response = await axios.get(
          `${API_BASE_URL}/participants/${participantId}`,
          {
            // Ruta GET /participants/:id
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const data = response.data;
        setDocumentNumber(data.document_number || "");
        setFullName(data.full_name || "");
        setEmail(data.email || "");
        setPhoneNumber(data.phone_number || "");
      } catch (err) {
        setError(
          err.response?.data?.error ||
            "Error al cargar los datos del participante."
        );
        console.error("Error fetching participant data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (token) {
      fetchParticipantData();
    }
  }, [participantId, token, API_BASE_URL]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setError("");
    setMessage("");
    setSuccessPopupMessage("");
    setShowSuccessPopup(false);

    if (!documentNumber.trim() || !fullName.trim()) {
      setError("El número de documento y el nombre completo son obligatorios.");
      setIsSaving(false);
      return;
    }

    const participantData = {
      document_number: documentNumber.trim(),
      full_name: fullName.trim(),
      email: email.trim() || null, // Guardar como null si está vacío
      phone_number: phoneNumber.trim() || null, // Guardar como null si está vacío
      // participant_type_id NO se envía aquí
    };

    try {
      let response;
      if (participantId) {
        // Modo edición (PUT)
        response = await axios.put(
          `${API_BASE_URL}/participants/${participantId}`,
          participantData,
          {
            // Ruta PUT /participants/:id
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setSuccessPopupMessage("Participante actualizado exitosamente.");
      } else {
        // Modo creación (POST)
        response = await axios.post(
          `${API_BASE_URL}/participants`,
          participantData,
          {
            // Ruta POST /participants
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setSuccessPopupMessage("Participante creado exitosamente.");
        // Limpiar formulario solo al crear
        setDocumentNumber("");
        setFullName("");
        setEmail("");
        setPhoneNumber("");
      }
      console.log(
        "Respuesta del backend (guardar participante):",
        response.data
      );
      setShowSuccessPopup(true);
    } catch (err) {
      setError(
        err.response?.data?.error || "Error al guardar el participante."
      );
      console.error(
        "Error saving participant:",
        err.response?.data || err.message
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleClosePopup = () => {
    setShowSuccessPopup(false);
    navigate("/admin/participants"); // Redirige a la lista de participantes
  };

  if (isLoading) {
    return (
      <div className="create-edit-participant-container">
        <p>Cargando datos...</p>
      </div>
    );
  }

  return (
    <div className="create-edit-participant-container">
      <Link to="/admin/participants" className="back-button">
        <span className="material-icons">arrow_back</span>
      </Link>

      <div className="create-edit-participant-card">
        <h2 className="create-edit-participant-title">
          {participantId ? "Editar Participante" : "Crear Nuevo Participante"}
        </h2>
        <p className="create-edit-participant-description">
          {participantId
            ? "Modifica la información de un participante existente."
            : "Registra un nuevo participante en el sistema."}
        </p>

        {message && (
          <p className="create-edit-participant-message success">{message}</p>
        )}
        {error && (
          <p className="create-edit-participant-message error">{error}</p>
        )}

        <form onSubmit={handleSubmit} className="create-edit-participant-form">
          <div className="form-section">
            <h3>Detalles del Participante</h3>
            <div className="form-group">
              <label htmlFor="documentNumber">Número de Documento:</label>
              <input
                type="text"
                id="documentNumber"
                value={documentNumber}
                onChange={(e) => setDocumentNumber(e.target.value)}
                className="participant-input"
                disabled={isSaving}
              />
            </div>
            <div className="form-group">
              <label htmlFor="fullName">Nombre Completo:</label>
              <input
                type="text"
                id="fullName"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="participant-input"
                disabled={isSaving}
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">Correo Electrónico:</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="participant-input"
                disabled={isSaving}
              />
            </div>
            <div className="form-group">
              <label htmlFor="phoneNumber">Número de Teléfono:</label>
              <input
                type="text"
                id="phoneNumber"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="participant-input"
                disabled={isSaving}
              />
            </div>
            {/* No hay campo para el Tipo de Participante aquí, ya que se asocia a certificados */}
          </div>

          <button
            type="submit"
            disabled={isSaving}
            className="create-edit-participant-submit-button"
          >
            {isSaving
              ? "Guardando..."
              : participantId
              ? "Actualizar Participante"
              : "Crear Participante"}
          </button>
        </form>
      </div>

      {/* Pop-up de éxito */}
      {showSuccessPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>¡Éxito!</h3>
            <p>{successPopupMessage}</p>
            <button onClick={handleClosePopup} className="popup-button">
              Cerrar y Volver a Participantes
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreateEditParticipantPage;
